/**
 * Utilities for exact-match document patch operations used by doc.edit.
 */

const SUPPORTED_DOCUMENT_EDIT_OPERATION_TYPES = new Set([
  'replace',
  'delete',
  'insert_after',
  'insert_before',
]);

/**
 * Convert arbitrary model output into a normalized patch operation list.
 * @param {unknown} operations - Raw operations value from the model.
 * @returns {{ operations: Array<{type: string, search: string, replace: string, all: boolean}>, errors: Array<object> }} Normalized operations and validation errors.
 */
export function normalizeDocumentEditOperations(operations) {
  const normalized = [];
  const errors = [];

  if (!Array.isArray(operations)) {
    return { operations: normalized, errors: [{ reason: 'operations_not_array' }] };
  }

  operations.forEach((operation, index) => {
    if (!operation || typeof operation !== 'object') {
      errors.push({ index, reason: 'invalid_operation' });
      return;
    }

    const type = String(operation.type || '').toLowerCase();
    if (!SUPPORTED_DOCUMENT_EDIT_OPERATION_TYPES.has(type)) {
      errors.push({ index, type, reason: 'unsupported_type' });
      return;
    }

    const search = typeof operation.search === 'string' ? operation.search : '';
    if (!search) {
      errors.push({ index, type, reason: 'missing_search' });
      return;
    }

    const replace =
      typeof operation.replace === 'string'
        ? operation.replace
        : (typeof operation.html === 'string' ? operation.html : '');

    normalized.push({
      type,
      search,
      replace,
      all: operation.all === true,
    });
  });

  return { operations: normalized, errors };
}

/**
 * Replace the first or every occurrence of a search string.
 * @param {string} html - Source HTML.
 * @param {string} search - Exact search string.
 * @param {string} replacement - Replacement string.
 * @param {boolean} replaceAll - Whether to replace every occurrence.
 * @returns {{ html: string, count: number }} Updated HTML and replacement count.
 */
function replaceExactOccurrences(html, search, replacement, replaceAll) {
  if (!search || !html.includes(search)) {
    return { html, count: 0 };
  }

  if (!replaceAll) {
    return { html: html.replace(search, replacement), count: 1 };
  }

  const parts = html.split(search);
  return {
    html: parts.join(replacement),
    count: parts.length - 1,
  };
}

/**
 * Apply exact-match HTML edit operations to a document string.
 * @param {string} documentHTML - HTML content to edit.
 * @param {Array<{type: string, search: string, replace?: string, html?: string, all?: boolean}>} operations - Exact-match operations.
 * @returns {{ html: string, appliedCount: number, failedOperations: Array<object>, appliedOperations: Array<object> }} Patch application result.
 */
export function applyDocumentEditOperations(documentHTML, operations) {
  let html = typeof documentHTML === 'string' ? documentHTML : '';
  const failedOperations = [];
  const appliedOperations = [];
  let appliedCount = 0;
  const { operations: normalizedOperations, errors } = normalizeDocumentEditOperations(operations);

  failedOperations.push(...errors);

  normalizedOperations.forEach((operation, index) => {
    const { type, search, replace, all } = operation;

    if (!html.includes(search)) {
      failedOperations.push({ index, type, reason: 'search_not_found' });
      return;
    }

    let result;
    if (type === 'replace') {
      result = replaceExactOccurrences(html, search, replace, all);
    } else if (type === 'delete') {
      result = replaceExactOccurrences(html, search, '', all);
    } else if (type === 'insert_after') {
      result = replaceExactOccurrences(html, search, `${search}${replace}`, all);
    } else {
      result = replaceExactOccurrences(html, search, `${replace}${search}`, all);
    }

    html = result.html;
    appliedCount += result.count;
    appliedOperations.push(operation);
  });

  return { html, appliedCount, failedOperations, appliedOperations };
}

/**
 * Wrap a deleted segment with diff-review delete markup (styled by the editor).
 * @param {string} html - Deleted HTML segment.
 * @param {string} changeId - Stable change id for accept/reject operations.
 * @returns {string} Wrapped HTML segment.
 */
function wrapDiffDelete(html, changeId) {
  return `<span data-diff-delete="true" data-change-id="${changeId}">${html}</span>`;
}

/**
 * Wrap an inserted segment with diff-review insert markup (styled by the editor).
 * @param {string} html - Inserted HTML segment.
 * @param {string} changeId - Stable change id for accept/reject operations.
 * @returns {string} Wrapped HTML segment.
 */
function wrapDiffInsert(html, changeId) {
  return `<span data-diff-insert="true" data-change-id="${changeId}">${html}</span>`;
}

/**
 * Build highlighted review HTML by replaying exact patch operations directly.
 * @param {string} originalHTML - The document HTML before editing.
 * @param {Array<{type: string, search: string, replace?: string, html?: string, all?: boolean}>} operations - Exact-match operations.
 * @returns {{ mergedHTML: string, changes: Array<{id: string, type: string}>, hasChanges: boolean, metrics: object, failedOperations: Array<object> }} Direct patch diff result.
 */
export function buildPatchDiffHtml(originalHTML, operations) {
  const startedAt =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();
  /**
   * Get a high-resolution timestamp when available.
   * @returns {number} Current timestamp in milliseconds.
   */
  const now = () =>
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();
  let mergedHTML = typeof originalHTML === 'string' ? originalHTML : '';
  const changes = [];
  const failedOperations = [];
  let changeId = 0;
  const { operations: normalizedOperations, errors } = normalizeDocumentEditOperations(operations);

  failedOperations.push(...errors);

  normalizedOperations.forEach((operation, index) => {
    const { type, search, replace, all } = operation;

    if (!mergedHTML.includes(search)) {
      failedOperations.push({ index, type, reason: 'search_not_found' });
      return;
    }

    /**
     * Create the highlighted replacement for this operation.
     * @returns {string} HTML replacement with diff review spans.
     */
    const createReplacement = () => {
      const cid = String(++changeId);
      if (type === 'replace') {
        changes.push({ id: cid, type: 'replace' });
        return `${wrapDiffDelete(search, cid)}${wrapDiffInsert(replace, cid)}`;
      }
      if (type === 'delete') {
        changes.push({ id: cid, type: 'delete' });
        return wrapDiffDelete(search, cid);
      }
      if (type === 'insert_after') {
        changes.push({ id: cid, type: 'insert' });
        return `${search}${wrapDiffInsert(replace, cid)}`;
      }
      changes.push({ id: cid, type: 'insert' });
      return `${wrapDiffInsert(replace, cid)}${search}`;
    };

    if (all) {
      mergedHTML = mergedHTML.split(search).join(createReplacement());
      return;
    }

    mergedHTML = mergedHTML.replace(search, createReplacement());
  });

  return {
    mergedHTML,
    changes,
    hasChanges: changes.length > 0 && failedOperations.length === 0,
    failedOperations,
    metrics: {
      diffMs: now() - startedAt,
      originalLength: typeof originalHTML === 'string' ? originalHTML.length : 0,
      editedLength: mergedHTML.length,
      tokenStrategy: 'patch',
      patchOperations: normalizedOperations.length,
    },
  };
}
